/* eslint-disable @typescript-eslint/no-explicit-any */
import InfiniteSelect from 'components/InfiniteSelect';
import {
  BaseFilter,
  BaseFilterConfigParams,
} from 'components/SearchAndFilters/FilterTypes';
import SelectedFilterBadge from 'components/SearchAndFilters/SelectedFiltersBar/SelectedFilterBadge';
import { useAtomValue } from 'jotai';
import { fetchHosts } from 'lib/helper';
import React from 'react';
import { companyIdAtom } from 'views/state';

import { sourceTypeAtom, SourceType } from './state';

export const getAssetsFetchConfig = (
  sourceType: SourceType,
  company_id: number,
  hostsIn?: number[],
) => {
  const [_, subType] = sourceType.split('__');
  let baseUrl = '';
  const additionalParams = {};

  switch (sourceType) {
    case SourceType.WAS:
      baseUrl = '/web_apps/filter/';
      additionalParams['company_id'] = company_id;
      break;
    case SourceType.SAST:
      baseUrl = '/sast/repositories/host/filter/';
      additionalParams['company_id'] = company_id;
      break;
    case SourceType.EP:
    case SourceType.EP_INTERNAL:
    case SourceType.EP_EXTERNAL:
    case SourceType.EP_EC2:
      baseUrl = '/endpoints/filter/';
      additionalParams['company_id'] = company_id;
      if (subType) additionalParams['scanner_type'] = subType;
      break;
    case SourceType.CA:
    case SourceType.CA_SERVER:
    case SourceType.CA_WORKSTATIONS:
      baseUrl = '/cloud_agents/filter/';
      additionalParams['company_id'] = company_id;
      if (subType) additionalParams['server_or_workstation'] = subType;
      break;
    //   default:
    //     baseUrl = null;
    //     break;
  }
  // if the current selected values is a number array
  // it means that we need to add an additional filter for fetch
  if (hostsIn?.length) {
    additionalParams['hostsIn'] = hostsIn;
  }

  return { baseUrl, additionalParams };
};

/**
 * Configuration for a custom filter.
 * Allows for custom filter rendering logic.
 */
export class HostsFilterConfig extends BaseFilter {
  badgeLabel: string;
  hostsData: any;

  constructor(
    params: BaseFilterConfigParams & {
      hostsData: any;
      badgeLabel: string;
    },
  ) {
    super({ ...params });
    this.badgeLabel = params.badgeLabel;
    this.hostsData = params.hostsData;
  }

  render(onSelectedValueChange: () => void) {
    return (
      <HostsFilter
        key={this.id}
        config={this}
        onSelectedValueChange={onSelectedValueChange}
      />
    );
  }

  renderFilterBadge(onSelectedValueChange: () => void): JSX.Element | null {
    const selectedHosts = this.getSelectedValues().map((host) => {
      // for sast hosts, display name/branch
      return host.label ? host.label : `${host.name}/${host.branch}`;
    });
    return (
      <SelectedFilterBadge
        label={this.badgeLabel}
        values={selectedHosts}
        onRemove={() => {
          this.resetSelectionToDefault();
          onSelectedValueChange();
        }}
      />
    );
  }
}

interface HostsFilterProps {
  config: HostsFilterConfig;
  onSelectedValueChange: () => void; // Callback for value changes
}

const HOSTS_LIMIT = 100;

const HostsFilter: React.FC<HostsFilterProps> = ({
  config,
  onSelectedValueChange,
}) => {
  const sourceType = useAtomValue(sourceTypeAtom);
  const company_id = useAtomValue(companyIdAtom);

  const { baseUrl, additionalParams } = getAssetsFetchConfig(
    sourceType as SourceType,
    company_id,
  );
  // Key is required for cache, if key gets changed, option will reload
  // Added company id with key to update the host options when company changes
  const key = sourceType + company_id;

  if (!sourceType) return null;

  return (
    <>
      <InfiniteSelect
        fetcherFn={(search, page) =>
          fetchHosts({
            baseUrl: baseUrl,
            search,
            page,
            limit: HOSTS_LIMIT,
            additionalParams: additionalParams,
            company_id: company_id,
          }).then((result) => {
            // if the current selected values is a number array
            // then we need to make appropriate selection
            if (
              config.currentSelectedValues?.every(
                (value) => typeof value === 'number',
              )
            ) {
              const selectedHosts = result?.results.filter((host) =>
                config.currentSelectedValues.includes(host.id),
              );
              config.currentSelectedValues = selectedHosts;
              onSelectedValueChange?.();
            }
            return result || { results: [], hasMore: false };
          })
        }
        filterKey="id"
        cacheKey={key}
        onChange={(selectedOptions) => {
          // eslint-disable-next-line no-console
          // console.log('selectedOptions:', selectedOptions);
          config.currentSelectedValues = selectedOptions;
          onSelectedValueChange?.();
        }}
        value={config.currentSelectedValues}
        isDisabled={sourceType === ''}
        defaultOptions={config.hostsData}
        placeholder="Search..."
        selectedLabel="Selected"
        optionsLabel="Host Assets"
        getOptionLabel={
          sourceType === SourceType.SAST
            ? (option) => `${option.name}/${option.branch}`
            : (option) =>
                option.alias
                  ? `${option.label} (${option.alias})`
                  : option.asset_name
                    ? `${option.label} (${option.asset_name})`
                    : option.label
        }
        getOptionValue={
          sourceType === SourceType.SAST
            ? (option) => option.id
            : (option) => option.value
        }
      />
    </>
  );
};

export default HostsFilter;
